import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const fetchUser = useCallback(async (tokenToUse) => {
    try {
      setLoading(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${tokenToUse}`;
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      fetchUser(storedToken);
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const login = async (email, password) => {
    try {
      console.log('🔵 FRONTEND: Starting login request');
      console.log('🔵 FRONTEND: API_URL =', API_URL);
      console.log('🔵 FRONTEND: Full URL =', `${API_URL}/api/auth/login`);
      console.log('🔵 FRONTEND: Email =', email);
      console.log('🔵 FRONTEND: Password length =', password?.length);
      
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      
      console.log('🟢 FRONTEND: Login response received');
      console.log('🟢 FRONTEND: Response status =', response.status);
      console.log('🟢 FRONTEND: Response data =', response.data);
      
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      
      console.log('🟢 FRONTEND: Login successful, user set');
      return userData;
    } catch (error) {
      console.error('🔴 FRONTEND: Login error');
      console.error('🔴 FRONTEND: Error message =', error.message);
      console.error('🔴 FRONTEND: Error response =', error.response?.data);
      console.error('🔴 FRONTEND: Error status =', error.response?.status);
      console.error('🔴 FRONTEND: Full error =', error);
      throw error;
    }
  };

  const googleLogin = async (credential) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/google-login`, { credential });
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      
      return userData;
    } catch (error) {
      console.error('Google login error:', error.response?.data);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, userData);
      const { token: newToken, user: newUser } = response.data;
      
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(newUser);
      
      return newUser;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API_URL}/api/auth/logout`);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
    }
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const value = {
    user,
    loading,
    login,
    googleLogin,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}